import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { EnvelopeSimple, Phone, MapPin, ArrowRight, CircleNotch } from "@phosphor-icons/react";
import { Reveal, Eyebrow, MaskLines } from "../components/Motion";
import { BUSINESS, SERVICES } from "../constants/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { name: "", email: "", phone: "", service: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please add your name, email and a short message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you — your request has been sent. We'll be in touch shortly.");
      setForm(empty);
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-[color:var(--border)] py-4 text-[color:var(--text)] placeholder:text-[color:var(--muted)] focus:border-[color:var(--gold)] focus:outline-none transition-colors duration-500";

  return (
    <div className="pt-36 sm:pt-44">
      <section className="max-w-[1440px] mx-auto px-5 sm:px-8">
        <Eyebrow>Start a project</Eyebrow>
        <h1 className="font-display font-light leading-[0.95] text-[clamp(2.8rem,8vw,7.5rem)] text-[color:var(--text)] mt-5">
          <MaskLines lines={["Request", "a Quote"]} delay={0.15} />
        </h1>
      </section>

      <section className="max-w-[1440px] mx-auto px-5 sm:px-8 py-20 sm:py-28 grid lg:grid-cols-[1.3fr_0.8fr] gap-16 lg:gap-24">
        <Reveal>
          <form onSubmit={submit} data-testid="contact-form" className="space-y-10">
            <div className="grid sm:grid-cols-2 gap-10">
              <div>
                <label className="font-mono-x text-[10px] tracking-[0.3em] uppercase text-[color:var(--muted)]">Your name *</label>
                <input data-testid="contact-name" className={inputCls} placeholder="Jane Doe" value={form.name} onChange={update("name")} />
              </div>
              <div>
                <label className="font-mono-x text-[10px] tracking-[0.3em] uppercase text-[color:var(--muted)]">Email *</label>
                <input data-testid="contact-email" type="email" className={inputCls} placeholder="you@email.com" value={form.email} onChange={update("email")} />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-10">
              <div>
                <label className="font-mono-x text-[10px] tracking-[0.3em] uppercase text-[color:var(--muted)]">Phone</label>
                <input data-testid="contact-phone" className={inputCls} placeholder="242-000-0000" value={form.phone} onChange={update("phone")} />
              </div>
              <div>
                <label className="font-mono-x text-[10px] tracking-[0.3em] uppercase text-[color:var(--muted)]">Service</label>
                <select data-testid="contact-service" className={`${inputCls} appearance-none cursor-pointer`} value={form.service} onChange={update("service")}>
                  <option value="" className="bg-[color:var(--surface)]">Select a service</option>
                  {SERVICES.map((s) => (
                    <option key={s.key} value={s.title} className="bg-[color:var(--surface)]">{s.title}</option>
                  ))}
                  <option value="Other" className="bg-[color:var(--surface)]">Other</option>
                </select>
              </div>
            </div>

            <div>
              <label className="font-mono-x text-[10px] tracking-[0.3em] uppercase text-[color:var(--muted)]">Tell us about your project *</label>
              <textarea data-testid="contact-message" rows={4} className={`${inputCls} resize-none`} placeholder="A few details about what you need and when…" value={form.message} onChange={update("message")} />
            </div>

            <button
              type="submit"
              data-testid="contact-submit-button"
              disabled={loading}
              className="group inline-flex items-center gap-3 rounded-full bg-[color:var(--gold)] px-9 py-4 text-[#0b0710] font-medium transition-all duration-500 hover:bg-[color:var(--gold-light)] disabled:opacity-60"
            >
              {loading ? <CircleNotch size={18} className="animate-spin" /> : null}
              {loading ? "Sending…" : "Send request"}
              {!loading && <ArrowRight className="transition-transform duration-500 group-hover:translate-x-1" size={18} weight="bold" />}
            </button>
          </form>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="border-t hairline pt-8 space-y-8">
            <p className="text-[color:var(--text-2)] leading-relaxed">
              Prefer to reach out directly? We'd love to hear from you.
            </p>
            <div className="space-y-5">
              <a href={`mailto:${BUSINESS.email}`} data-testid="contact-email-link" className="flex items-start gap-4 group">
                <span className="h-11 w-11 rounded-full border hairline flex items-center justify-center text-[color:var(--gold)] group-hover:border-[color:var(--gold)] transition-colors"><EnvelopeSimple size={18} /></span>
                <span className="text-[color:var(--text)] break-all pt-2.5 group-hover:text-[color:var(--gold)] transition-colors">{BUSINESS.email}</span>
              </a>
              {BUSINESS.phones.map((p) => (
                <a key={p} href={`tel:${p.replace(/-/g, "")}`} className="flex items-center gap-4 group">
                  <span className="h-11 w-11 rounded-full border hairline flex items-center justify-center text-[color:var(--gold)] group-hover:border-[color:var(--gold)] transition-colors"><Phone size={18} /></span>
                  <span className="text-[color:var(--text)] group-hover:text-[color:var(--gold)] transition-colors">{p}</span>
                </a>
              ))}
              <div className="flex items-center gap-4">
                <span className="h-11 w-11 rounded-full border hairline flex items-center justify-center text-[color:var(--gold)]"><MapPin size={18} /></span>
                <span className="text-[color:var(--text)]">{BUSINESS.location}</span>
              </div>
            </div>
            <p className="font-display italic text-3xl text-gradient-gold pt-4">{BUSINESS.tagline}</p>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
